<script setup lang="ts">
import type { Collections } from '@nuxt/content'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'

const root = ref<HTMLElement>()
let media: gsap.MatchMedia | undefined

onMounted(() => {
  gsap.registerPlugin(ScrollTrigger, SplitText)
  media = gsap.matchMedia()

  media.add('(prefers-reduced-motion: no-preference)', () => {
    const q = gsap.utils.selector(root.value)
    root.value!.classList.add('blog-ready')

    const title = SplitText.create(q('.blog-title')[0], { type: 'words,chars', mask: 'chars' })
    gsap.timeline({ defaults: { ease: 'expo.out', lazy: false } })
      // The mask would keep clipping descenders and overhangs once the title is in place.
      .from(title.chars, { yPercent: 110, duration: 1.1, stagger: 0.04, onComplete: () => title.revert() }, 0)
      .from(q('.blog-rule'), { scaleX: 0, duration: 1.2, ease: 'power3.inOut' }, 0.3)
      .from(q('.blog-intro > *'), { y: 16, opacity: 0, duration: 0.7, stagger: 0.08, ease: 'power3.out', clearProps: 'all' }, 0.5)

    q('.blog-item').forEach((item) => {
      const inside = gsap.utils.selector(item)
      gsap.timeline({ scrollTrigger: { trigger: item, start: 'top 85%', once: true }, defaults: { ease: 'power3.out' } })
        .from(inside('.blog-cover'), { clipPath: 'inset(10% 10% 10% 10% round 8px)', opacity: 0, duration: 1, ease: 'expo.out', clearProps: 'clipPath,opacity' }, 0)
        .from(inside('.blog-text > *'), { y: 20, opacity: 0, duration: 0.6, stagger: 0.07, clearProps: 'all' }, 0.15)
    })

    return () => {
      title.revert()
      root.value?.classList.remove('blog-ready')
    }
  }, root.value)
})

onBeforeUnmount(() => media?.revert())

const searchedTags = ref<string[]>([])
const searchedTitle = ref('')

const { locale } = useI18n()

const { data: articles } = await useAsyncData('articles-' + locale.value, async () => {
  const collection = ('articles_' + locale.value) as keyof Collections
  return await queryCollection(collection).all() as Collections['articles_en'][] | Collections['articles_fr'][]
}, {
  watch: [locale],
})

if (!articles.value)
  throw createError({ statusCode: 404, statusMessage: 'Page not found' })

// Most recent first.
const sorted = computed(() => [...(articles.value ?? [])].sort((a, b) => String(b.date ?? '').localeCompare(String(a.date ?? ''))))

const tags = computed(() => Array.from(new Set(sorted.value.flatMap(article => article.tags ?? []))))

const filteredArticles = computed(() => sorted.value.filter(article =>
  (searchedTags.value.length === 0 || searchedTags.value.some(tag => article.tags?.includes(tag)))
  && (searchedTitle.value === '' || article.title!.toLowerCase().includes(searchedTitle.value.toLowerCase())),
))

const toggleTag = (tag: string) => {
  searchedTags.value = searchedTags.value.includes(tag)
    ? searchedTags.value.filter(t => t !== tag)
    : [...searchedTags.value, tag]
}

// The list changes height when filtered: the triggers below it must be measured again.
watch(filteredArticles, () => nextTick(() => ScrollTrigger.refresh()))

const formatDate = (date?: string) => {
  if (!date) return ''
  const parsed = new Date(date)
  return Number.isNaN(parsed.getTime())
    ? date
    : new Intl.DateTimeFormat(locale.value, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(parsed)
}
</script>

<template>
  <section
    ref="root"
    class="blog-root mx-auto max-w-7xl overflow-x-clip px-4 pb-24 sm:px-6 lg:px-8"
  >
    <header class="pt-10 sm:pt-24">
      <h1 class="blog-in blog-title whitespace-nowrap text-[clamp(4rem,17vw,14rem)] font-semibold leading-[0.95] tracking-tighter text-white">
        <slot
          name="title"
          mdc-unwrap="p"
        />
      </h1>
      <div class="blog-in blog-rule mt-6 h-px origin-left bg-white/20" />
      <div class="blog-in blog-intro mt-5 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <p class="text-white/60 sm:text-lg">
          <slot
            name="subtitle"
            mdc-unwrap="p"
          />
        </p>
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
          <ul class="flex flex-wrap gap-2">
            <li>
              <button
                type="button"
                class="cursor-pointer rounded-full border px-3 py-1 text-sm transition-colors"
                :class="searchedTags.length === 0 ? 'border-white bg-white text-neutral-950' : 'border-white/10 bg-white/5 text-white/70 hover:text-white'"
                @click="searchedTags = []"
              >
                {{ $t("writing.all_tags") }}
              </button>
            </li>
            <li
              v-for="tag of tags"
              :key="tag"
            >
              <button
                type="button"
                :aria-pressed="searchedTags.includes(tag)"
                class="cursor-pointer rounded-full border px-3 py-1 text-sm transition-colors"
                :class="searchedTags.includes(tag) ? 'border-white bg-white text-neutral-950' : 'border-white/10 bg-white/5 text-white/70 hover:text-white'"
                @click="toggleTag(tag)"
              >
                {{ tag }}
              </button>
            </li>
          </ul>
          <input
            v-model="searchedTitle"
            type="search"
            :placeholder="$t('writing.search_article')"
            :aria-label="$t('writing.search_article')"
            class="w-full border-b border-white/20 bg-transparent py-1 text-sm text-white outline-none transition-colors placeholder:text-white/40 focus:border-white sm:w-56"
          >
        </div>
      </div>
    </header>

    <ul
      v-if="filteredArticles.length"
      class="mt-8"
    >
      <li
        v-for="article of filteredArticles"
        :key="article.path"
        class="blog-item border-b border-white/10"
      >
        <NuxtLink
          :to="article.path"
          class="group grid items-center gap-8 py-12 lg:grid-cols-12 lg:gap-16 lg:py-16"
        >
          <div class="blog-cover aspect-[16/10] overflow-hidden rounded-lg border border-white/10 bg-neutral-900 lg:col-span-5">
            <img
              :src="article.image"
              alt=""
              loading="lazy"
              class="size-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            >
          </div>
          <div class="blog-text lg:col-span-7">
            <p class="text-sm text-white/50">
              <time :datetime="article.date">{{ formatDate(article.date) }}</time>
              <template v-if="article.readingTime">
                · {{ article.readingTime }}
              </template>
            </p>
            <h2 class="mt-3 text-pretty text-3xl font-semibold leading-[1.1] tracking-tight text-white sm:text-4xl xl:text-5xl">
              {{ article.title }}
            </h2>
            <p class="mt-4 max-w-2xl text-pretty leading-relaxed text-white/60">
              {{ article.description }}
            </p>
            <div class="mt-5 flex items-center justify-between gap-6">
              <ul class="flex flex-wrap gap-2">
                <li
                  v-for="tag in article.tags"
                  :key="tag"
                  class="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70"
                >
                  {{ tag }}
                </li>
              </ul>
              <span class="flex size-11 shrink-0 items-center justify-center rounded-full bg-white text-neutral-950 transition-transform duration-500 group-hover:-rotate-45">
                <UIcon
                  name="heroicons:arrow-right"
                  class="size-5"
                />
              </span>
            </div>
          </div>
        </NuxtLink>
      </li>
    </ul>
    <div
      v-else
      class="flex h-64 flex-col items-center justify-center gap-2 text-center"
    >
      <span class="text-2xl font-semibold text-white">
        {{ $t("writing.not_found") }}
      </span>
      <span class="max-w-md text-sm text-white/60">
        {{ $t("writing.not_found_description") }}
      </span>
    </div>
  </section>
</template>

<style>
  /* Hidden until the entrance starts, with a fallback in case the script never runs. */
  @media (prefers-reduced-motion: no-preference) {
    .blog-root:not(.blog-ready) .blog-in {
      opacity: 0;
      animation: blog-fallback 0.3s 2.5s forwards;
    }
  }

  @keyframes blog-fallback {
    to {
      opacity: 1;
    }
  }
</style>
